import {isEqualWith, last} from 'lodash';
import React, {ComponentProps, FC, useState} from 'react';
import {useDrop} from 'react-dnd';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';
import styled from 'styled-components';

import {HandlerOf, PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {buildCustomViewCounterKey} from '../../../helpers/counterHelpers';
import {buildCustomViewPath, VisualCustomView} from '../../../helpers/models/customViewHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {wrapActionCreator} from '../../../helpers/reduxHelpers';
import {makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {
  buildCustomViewExpandedKey,
  computeVisualCustomViewIsEqual,
  defaultIsCustomViewExpanded,
} from '../../../helpers/visualCustomViewHelpers';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {InboxesFiltersEnum} from '../../../models/url/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  SettingsTagTypesEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceTagNestTypesEnum,
} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {Dispatch} from '../../../store/appStore';
import {getHasPermission} from '../../../store/data/access/accessSelectors';
import {canMoveToTagAction, moveToTagAction} from '../../../store/data/conversations/tagConversationsActions';
import {toggleExpandItemAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {toggleHideTagAction} from '../../../store/data/preferences/preferencesActions';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {
  WorkspaceDropNestTagTargetRelationshipsEnum,
  WorkspaceDropReorderTagHandler,
  WorkspaceTagDropNestHandler,
} from '../../../store/data/workspace/reorder/types';
import {VisualWorkspaceRecentlyViewedMap} from '../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {hideWorkspaceTagAsyncAction} from '../../../store/data/workspace/workspaceActions';
import {
  navigationLineChevronWidth,
  navigationLineIndentPaddingFactor,
  navigationLineMaximumIndentLevel,
} from '../../common/navigation/navigationLine';
import {canDropWorkspaceItemAtTarget} from './dnd/workspaceDragDropHelpers';
import {
  DropLinePositionsEnum,
  isWorkspaceDragItem,
  WorkspaceDragItem,
  WorkspaceDragSourceTypesEnum,
  WorkspaceItemDropHandler,
  WorkspacePersonalCustomViewDragItem,
} from './dnd/workspaceDragDropTypes';
import {maybeAddWorkspaceDropLineStyle} from './line/workspaceLineStyles';
import {NestingTeamTagDialogConfirmationContainer} from './nestingTeamTagDialogConfirmationContainer';
import {WorkspaceCustomViewItemContainer} from './workspaceCustomViewItemContainerBase';
import {WorkspaceItemContainerProps} from './workspaceItemContainerHelpers';

/*
 * Props.
 */

interface WorkspaceCustomViewSubtreeContainerProps extends WorkspaceItemContainerProps {
  customView: VisualCustomView;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  /**
   * An optional click handler.
   * Note that the component already links to the custom view when not editing the workspace.
   */
  onClick?: HandlerOf<VisualCustomView>;
  onToggleExpandInEditor?: HandlerOf<VisualCustomView>;
  onDropReorderCustomView: WorkspaceDropReorderTagHandler;
  onDropNestCustomView: WorkspaceTagDropNestHandler;
  canDrag: boolean;
  isLastItem: boolean;
}

interface WorkspaceCustomViewSubtreeProps
  extends Omit<ComponentProps<typeof WorkspaceCustomViewItemContainer>, 'hasExpandedChildren'> {
  counterDelegationId?: number;
  onToggleExpandInEditor?: HandlerOf<VisualCustomView>;
  onDropReorderCustomView: WorkspaceDropReorderTagHandler;
  onDropNestCustomView: WorkspaceTagDropNestHandler;
  recentlyViewedMap?: VisualWorkspaceRecentlyViewedMap;
}

/*
 * Constants.
 */

const customViewKey: keyof WorkspaceCustomViewSubtreeContainerProps = 'customView';

/*
 * Style.
 */

interface WorkspaceCustomViewStyleProps {
  $isHidden?: boolean;
}
const StyledWorkspaceCustomViewDiv = styled.div<WorkspaceCustomViewStyleProps>`
  display: ${(p) => (p.$isHidden ? 'none' : 'initial')};
`;

const StyledLastDropTargetDiv = styled.div`
  height: 10px;
  margin: 0 5px;
  margin-bottom: -10px;
  position: relative;

  ${maybeAddWorkspaceDropLineStyle({
    indentLevel: 0,
    chevronWidth: navigationLineChevronWidth,
    indentPaddingFactor: navigationLineIndentPaddingFactor,
    maximumIndentLevel: navigationLineMaximumIndentLevel,
  })};
`;

/*
 * Component.
 */

export const WorkspaceCustomViewSubtree: FC<WorkspaceCustomViewSubtreeProps> = (props) => {
  const {panelId, customView, onDropReorderCustomView, onDropNestCustomView} = props;
  const {isExpanded, children, isVisibleInWorkspace} = customView;

  const isTeammateView = isTeammateNamespace(customView.namespace);
  const customViewPath = props.isEditingWorkspace
    ? undefined
    : buildCustomViewPath(customView.id, {
        isTeammateView,
        filter: InboxesFiltersEnum.OPEN,
      });
  const [nestingAttempt, setNestingAttempt] = useState<
    NestingAttemptError | UnnestingAttemptError | undefined
  >(undefined);

  // If our parent custom view is the last one in the list AND has expanded children, grab the last child.
  const endOfListChild = props.canDrag && props.isLastItem && isExpanded ? last(children) : undefined;

  // Create a drag item to target the end of the list.
  const endOfListDragItem = endOfListChild && {
    type: WorkspaceDragSourceTypesEnum.PERSONAL_VIEW,
    hasExpandedChildren: false,
    namespace: endOfListChild.namespace,
    nestingLevel: 0,
    id: endOfListChild.id,
    isLast: true,
  };

  // Build a simplified version of the `useDrop` handler from useWorkspaceDragAndDrop.
  const [collectedProps, dropRef] = useDrop<WorkspaceDragItem, void, {isOver: boolean}>({
    accept: [WorkspaceDragSourceTypesEnum.PERSONAL_VIEW],
    canDrop: (item) => isWorkspaceDragItem(item),
    drop: (item, monitor) => {
      if (!monitor.isOver() || !monitor.canDrop() || !props.onWorkspaceItemDrop || !endOfListDragItem) {
        return;
      }

      try {
        props.onWorkspaceItemDrop(
          item,
          DropLinePositionsEnum.BELOW,
          endOfListDragItem,
          Boolean(nestingAttempt),
        );
      } catch (err) {
        if (err instanceof NestingAttemptError || err instanceof UnnestingAttemptError) {
          setNestingAttempt(err);
          return;
        }
        throw err;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
    }),
  });

  function maybeRenderNestingTeamTagDialog() {
    if (!nestingAttempt) {
      return null;
    }

    return (
      <NestingTeamTagDialogConfirmationContainer
        newParentTagId={
          nestingAttempt instanceof UnnestingAttemptError ? undefined : nestingAttempt.targetItem.id
        }
        shouldNestNextToParent={nestingAttempt.shouldNestNextToParent}
        targetTagId={nestingAttempt.droppedItem.id}
        onOkClick={() => {
          setNestingAttempt(undefined);

          return (
            props.onWorkspaceItemDrop &&
            props.onWorkspaceItemDrop(
              nestingAttempt.droppedItem,
              nestingAttempt.position,
              nestingAttempt.targetItem,
              true,
            )
          );
        }}
        onRequestClose={() => {
          setNestingAttempt(undefined);
        }}
        isEditingWorkspace={props.isEditingWorkspace}
      />
    );
  }

  return (
    <>
      <StyledWorkspaceCustomViewDiv $isHidden={props.customView.isHidden}>
        {/* Render our custom view. */}
        <WorkspaceCustomViewItemContainer
          {...props}
          to={customViewPath}
          isChecked={isVisibleInWorkspace}
          canDrag={props.canDrag}
          hasExpandedChildren={isExpanded && children.length > 0}
          isLastItem={props.isLastItem}
          onWorkspaceItemDrop={(dropped, position, target) => {
            if (!props.onWorkspaceItemDrop) {
              return;
            }

            try {
              props.onWorkspaceItemDrop(dropped, position, target, Boolean(nestingAttempt));
            } catch (err) {
              if (err instanceof NestingAttemptError || err instanceof UnnestingAttemptError) {
                setNestingAttempt(err);
                return;
              }
              throw err;
            }
          }}
          isRecentlyViewed={
            props.recentlyViewedMap ? props.recentlyViewedMap[props.customView.id] : undefined
          }
        />

        {/* And all of its children. */}
        {isExpanded &&
          children.map((childVisualCustomView, index) => (
            <WorkspaceCustomViewSubtreeContainer
              key={childVisualCustomView.id}
              panelId={panelId}
              customView={childVisualCustomView}
              counterKey={buildCustomViewCounterKey(childVisualCustomView.uid, childVisualCustomView.type)}
              counterDelegationId={props.counterDelegationId}
              canDrag={props.canDrag}
              isLastItem={index === children.length - 1}
              onDropReorderCustomView={onDropReorderCustomView}
              onDropNestCustomView={onDropNestCustomView}
              isEditingWorkspace={props.isEditingWorkspace}
              onClick={props.onClick}
              onToggleExpandInEditor={props.onToggleExpandInEditor}
              shouldAlphabetizeWorkspace={props.shouldAlphabetizeWorkspace}
              recentlyViewedMap={props.recentlyViewedMap}
            />
          ))}
        {/* Maybe render a drop zone for the end of the list. */}
        {endOfListDragItem && (
          <StyledLastDropTargetDiv
            data-line-position={collectedProps.isOver && DropLinePositionsEnum.ABOVE}
            ref={dropRef}
          />
        )}
      </StyledWorkspaceCustomViewDiv>
      {maybeRenderNestingTeamTagDialog()}
    </>
  );
};

/*
 * Selectors.
 */

function getCustomView(_state: AppState, props: WorkspaceCustomViewSubtreeContainerProps) {
  return props.customView;
}

function getIsEditingWorkspace(_state: AppState, props: WorkspaceCustomViewSubtreeContainerProps) {
  return props.isEditingWorkspace;
}

function makeGetHasEditPermission() {
  return createSelector([getHasPermission, getCustomView], (hasPermission, customView) =>
    hasPermission(customView.tag.namespace, PermissionsEnum.EDIT_TAG),
  );
}

function makeGetShouldHideMoreMenu() {
  return createSelector(
    [getIsEditingWorkspace, getUserNamespace, getCustomView],
    (isEditingWorkspace, userNamespace, customView) =>
      isEditingWorkspace ||
      (isTeammateNamespace(customView.namespace) && userNamespace !== customView.namespace),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<WorkspaceCustomViewSubtreeContainerProps>()(() => ({
  hasEditPermission: makeGetHasEditPermission(),
  shouldHideMoreMenu: makeGetShouldHideMoreMenu(),
}));

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    toggleExpandItem: wrapActionCreator(dispatch, toggleExpandItemAction),
    toggleHideTag: wrapActionCreator(dispatch, toggleHideTagAction),
    hideWorkspaceTag: wrapActionCreator(dispatch, hideWorkspaceTagAsyncAction),
    canMoveToTag: wrapActionCreator(dispatch, canMoveToTagAction),
    moveToTag: wrapActionCreator(dispatch, moveToTagAction),
  };
}

export const WorkspaceCustomViewSubtreeContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {customView, onDropReorderCustomView, onDropNestCustomView, isEditingWorkspace} = ownProps;

    const onToggleExpandClick = () => {
      if (ownProps.isEditingWorkspace) {
        if (ownProps.onToggleExpandInEditor) {
          ownProps.onToggleExpandInEditor(customView);
        }
      } else {
        trackInterfaceEvent(InterfaceEventsEnum.P1_TAG_CHEVRON_CLICK, {
          tag_id: customView.tag.id,
          action: customView.isExpanded
            ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
            : WorkspaceDisplayChevronActionsEnum.EXPAND,
        });
        dispatchProps.toggleExpandItem(
          buildCustomViewExpandedKey(customView.id),
          defaultIsCustomViewExpanded,
        );
      }
    };

    const onHideClick = () => {
      if (ownProps.isEditingWorkspace) {
        return;
      }

      dispatchProps.hideWorkspaceTag(customView.tag.id, customView.tag.isView, customView.tag.namespace);
      dispatchProps.toggleHideTag(customView.tag.id, [customView.tag], {value: false});
    };

    const canConversationsDrop: PredicateOf<CellsConversationsDragItem> = (options) =>
      ownProps.isEditingWorkspace
        ? false
        : dispatchProps.canMoveToTag(options, customView.tag.id, customView.tag.namespace);

    const onConversationsDrop: HandlerOf<CellsConversationsDragItem> = (options) => {
      if (ownProps.isEditingWorkspace) {
        return;
      }
      dispatchProps.moveToTag(options, customView.tag.id);
    };

    const onWorkspaceItemDrop: WorkspaceItemDropHandler = (droppedItem, linePosition, target, skipError) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target, true)) {
        return;
      }

      if (
        target.type !== WorkspaceDragSourceTypesEnum.PERSONAL_VIEW ||
        droppedItem.type !== WorkspaceDragSourceTypesEnum.PERSONAL_VIEW
      ) {
        return;
      }

      if (!skipError) {
        maybeThrowError(droppedItem, linePosition, target);
      }

      const commonTrackingProps = {
        tag_type: isTeamNamespace(droppedItem.namespace)
          ? SettingsTagTypesEnum.TEAM
          : SettingsTagTypesEnum.INDIVIDUAL,
        tag_id: droppedItem.id,
      };

      const nestEvent = isEditingWorkspace
        ? InterfaceEventsEnum.P1_EDIT_NEST
        : InterfaceEventsEnum.P1_TAG_UPDATE_NEST;

      // Dropping directly onto another custom view item.
      if (linePosition === DropLinePositionsEnum.NONE) {
        onDropNestCustomView({
          childTagId: droppedItem.id,
          targetTagId: target.id,
          targetRelationship: WorkspaceDropNestTagTargetRelationshipsEnum.PARENT,
          customView,
        });
        trackInterfaceEvent(nestEvent, {
          update_type: WorkspaceTagNestTypesEnum.NEST_UNDER,
          ...commonTrackingProps,
        });

        return;
      }

      const isSameNamespace = target.namespace === droppedItem.namespace;

      // Dropping inside of an opened parent tag (only when tags are in the same namespace).
      if (target.nestingLevel !== droppedItem.nestingLevel) {
        if (
          linePosition === DropLinePositionsEnum.BELOW &&
          target.hasExpandedChildren &&
          target.nestingLevel === 0 &&
          isSameNamespace
        ) {
          trackInterfaceEvent(nestEvent, {
            update_type: WorkspaceTagNestTypesEnum.NEST_UNDER,
            ...commonTrackingProps,
          });
          onDropNestCustomView({
            childTagId: droppedItem.id,
            targetTagId: target.id,
            targetRelationship: WorkspaceDropNestTagTargetRelationshipsEnum.PARENT,
            customView,
          });
        } else if (target.nestingLevel === 0) {
          // Dropping at the root level
          trackInterfaceEvent(nestEvent, {
            update_type: WorkspaceTagNestTypesEnum.UNNEST,
            ...commonTrackingProps,
          });
          onDropNestCustomView({
            childTagId: droppedItem.id,
            targetTagId: undefined,
            targetRelationship: WorkspaceDropNestTagTargetRelationshipsEnum.SIBLING,
          });
        } else if (isSameNamespace) {
          // Dropping inside a parent tag
          trackInterfaceEvent(nestEvent, {
            update_type: WorkspaceTagNestTypesEnum.NEST_UNDER,
            ...commonTrackingProps,
          });
          onDropNestCustomView({
            childTagId: droppedItem.id,
            targetTagId: target.id,
            targetRelationship: WorkspaceDropNestTagTargetRelationshipsEnum.SIBLING,
          });
        }
      }

      onDropReorderCustomView({
        droppedItemId: droppedItem.id,
        targetItemId: target.id,
        linePosition,
      });
    };

    return {
      ...ownProps,
      ...stateProps,
      onToggleExpandClick,
      onHideClick,
      canConversationsDrop,
      onConversationsDrop,
      onWorkspaceItemDrop,
    };
  },
  {
    areOwnPropsEqual: areContainerPropsEqual,
  },
)(WorkspaceCustomViewSubtree);

function areContainerPropsEqual(
  nextProps: WorkspaceCustomViewSubtreeContainerProps,
  prevProps: WorkspaceCustomViewSubtreeContainerProps,
): boolean {
  return isEqualWith(nextProps, prevProps, (nextValue, prevValue, key, parent) => {
    // If we're not comparing a prop value, use the default behavior.
    if (parent !== nextProps) {
      return undefined;
    }

    // If we're comparing a visual custom view object, use custom equality. These objects are created
    // dynamically in selectors and therefore their object identity changes frequently.
    if (key === customViewKey) {
      return computeVisualCustomViewIsEqual(nextValue, prevValue);
    }

    // For all other prop values, use identity equality.
    return nextValue === prevValue;
  });
}

function maybeThrowError(
  droppedItem: WorkspacePersonalCustomViewDragItem,
  linePosition: DropLinePositionsEnum,
  target: WorkspacePersonalCustomViewDragItem,
) {
  const isActingOnTeamTag = isTeamNamespace(droppedItem.namespace) && isTeamNamespace(target.namespace);

  if (droppedItem.nestingLevel === target.nestingLevel && linePosition !== DropLinePositionsEnum.NONE) {
    return;
  }

  if (isActingOnTeamTag) {
    if (linePosition === DropLinePositionsEnum.NONE) {
      throw new NestingAttemptError(droppedItem, linePosition, target, false);
    }

    // Dropping inside of an opened parent tag
    if (
      linePosition === DropLinePositionsEnum.BELOW &&
      target.hasExpandedChildren &&
      target.nestingLevel === 0
    ) {
      throw new NestingAttemptError(droppedItem, linePosition, target, false);
    } else if (target.nestingLevel === 0) {
      // Dropping at the root level
      throw new UnnestingAttemptError(droppedItem, linePosition, target, true);
    }
    // Dropping inside a parent tag
    else {
      throw new NestingAttemptError(droppedItem, linePosition, target, true);
    }
  }
}

class NestingAttemptError extends CustomError {
  constructor(
    droppedItem: WorkspacePersonalCustomViewDragItem,
    position: DropLinePositionsEnum,
    targetItem: WorkspacePersonalCustomViewDragItem,
    shouldNestNextToParent: boolean,
  ) {
    super();
    this.droppedItem = droppedItem;
    this.position = position;
    this.targetItem = targetItem;
    this.shouldNestNextToParent = shouldNestNextToParent;
  }
  readonly droppedItem: WorkspacePersonalCustomViewDragItem;
  readonly position: DropLinePositionsEnum;
  readonly targetItem: WorkspacePersonalCustomViewDragItem;
  readonly shouldNestNextToParent: boolean;
}

class UnnestingAttemptError extends NestingAttemptError {}
